import { supabase } from './supabase.js';

// Sentinel for "is Razorpay configured at all?" Razorpay is the only
// payment method — if the key is missing, checkout shows a "payments
// temporarily unavailable" notice and Place Order is disabled.
export const razorpayConfigured =
  !!import.meta.env.VITE_RAZORPAY_KEY_ID;

// Lazy-load Razorpay Checkout only when payment is actually needed (checkout),
// instead of on every page. Keeps ~30KB + Razorpay's preloaded chunks off the
// home/product/category pages. Idempotent — the script loads at most once.
let razorpayScriptPromise = null;
export function loadRazorpayScript() {
  if (typeof window.Razorpay === 'function') return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = resolve;
    s.onerror = () => {
      razorpayScriptPromise = null; // allow a retry on the next attempt
      reject(new Error('Could not load the payment library. Check your connection and try again.'));
    };
    document.head.appendChild(s);
  });
  return razorpayScriptPromise;
}

/**
 * Open Razorpay Checkout for an existing order row. Returns a promise
 * that resolves once the payment is verified server-side, or rejects
 * if the user dismisses the modal / payment fails / signature check fails.
 *
 * The amount is NOT taken from the client — `create-razorpay-order`
 * reads it from the database (orders.total, populated by triggers from
 * products.price). The browser only sends `order_id`.
 *
 * @param {object} args
 * @param {object} args.order       The order row from placeOrder()
 * @param {string} args.customerName
 * @param {string} args.customerEmail
 * @param {string} args.customerPhone
 */
export async function payOrderWithRazorpay({ order, customerName, customerEmail, customerPhone }) {
  // Ensure the Checkout script is present (lazy-loaded on demand).
  await loadRazorpayScript();
  if (typeof window.Razorpay !== 'function') {
    throw new Error('Razorpay Checkout script not loaded. Refresh the page and try again.');
  }

  // 1. Ask the Edge Function to mint a Razorpay order for this row.
  const { data: rz, error: rzErr } = await supabase.functions.invoke(
    'create-razorpay-order',
    { body: { order_id: order.id } },
  );
  if (rzErr || !rz?.razorpay_order_id) {
    throw new Error(rz?.error || rzErr?.message || 'Could not start payment');
  }

  // 2. Open Razorpay Checkout. The user pays inside this modal.
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key:       rz.key_id,
      order_id:  rz.razorpay_order_id,
      amount:    rz.amount_paise,
      currency:  rz.currency,
      name:      'Sunnah Enterprises',
      description: `Order #${String(order.id).slice(0, 8).toUpperCase()}`,
      image:     '/images/logo/sunnah_entrprises_logo-2.jpg?v=2',
      prefill: {
        name:    customerName  || '',
        email:   customerEmail || '',
        contact: customerPhone || '',
      },
      notes:   { supabase_order_id: order.id },
      theme:   { color: '#1f6f43' },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
      handler: async (resp) => {
        // 3. Verify the signature server-side before treating as paid.
        const { data: vr, error: vErr } = await supabase.functions.invoke(
          'verify-razorpay-payment',
          {
            body: {
              order_id: order.id,
              razorpay_order_id:   resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature:  resp.razorpay_signature,
            },
          },
        );
        if (vErr || !vr?.ok) {
          reject(new Error(vr?.error || vErr?.message || 'Payment verification failed'));
          return;
        }
        resolve({
          razorpay_payment_id: resp.razorpay_payment_id,
          status: vr.status,
          payment_status: vr.payment_status,
        });
      },
    });

    rzp.on?.('payment.failed', (e) => {
      reject(new Error(e?.error?.description || 'Payment failed'));
    });

    rzp.open();
  });
}
